import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  register as apiRegister,
  resendVerificationEmail as apiResendVerification,
} from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { AuthPageFrame } from '../components/AuthPageFrame.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_ON_LOWEST } from '../styles/forms.ts';

/**
 * Tenant registration form. Calls `POST /v1/auth/register` which
 * creates the account + owning org atomically and sends a verification
 * email (platform-api side — see `apps/platform-api/src/routes/v1-auth.ts`).
 *
 * Post-submit flow: show a "check your email" confirmation state
 * rather than navigating away. The link arrives asynchronously; the
 * user returns via `/verify-email?token=...` later. We do NOT auto-log
 * them in — the login step happens after verification.
 *
 * Password rules mirror platform-api: 10–128 chars. We surface a
 * client-side hint before submit; the server is the source of truth.
 *
 * Failed registrations surface both an inline error (keeps the caller's
 * focus on the form) and a toast (survives if the user scrolls / the
 * modal collapses).
 */

export function Register(): JSX.Element {
  const { status, fetchImpl } = useSession();
  const toast = useToast();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Resend flow — decoupled from initial submit. `resendSubmitting` gates
  // the button, `resendAck` flips copy to "we just sent another" so the
  // user can tell the second click actually did something. The backend
  // never discloses whether the email matched, so we do not distinguish.
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendAck, setResendAck] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0 || password.length === 0) {
      setError(t('register.error.empty_fields'));
      return;
    }
    if (password.length < 10) {
      setError(t('register.error.password_too_short'));
      return;
    }
    setSubmitting(true);
    try {
      await apiRegister(
        {
          email: trimmedEmail,
          password,
          ...(displayName.trim().length > 0 ? { displayName: displayName.trim() } : {}),
        },
        fetchImpl,
      );
      setSuccess(trimmedEmail);
      setResendAck(false);
      setEmail('');
      setPassword('');
      setDisplayName('');
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    if (success === null) {
      return;
    }
    setResendSubmitting(true);
    try {
      await apiResendVerification({ email: success }, fetchImpl);
      setResendAck(true);
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      toast.error(message);
    } finally {
      setResendSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      eyebrow={t('register.eyebrow')}
      title={t('register.subtitle')}
      description={t('register.description')}
      testId="register-page-frame"
      footer={
        <div className="text-xs text-outline">
          <Link
            to="/login"
            data-testid="register-login-link"
            className="rounded-lg hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('register.already_have_account')}
          </Link>
        </div>
      }
    >

        {success !== null ? (
          <div
            className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-6"
            data-testid="register-success"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
              {t('register.success.title')}
            </span>
            <p className="text-sm text-on-surface">
              {t('register.success.body', { email: success })}
            </p>
            {resendAck ? (
              <p
                className="text-xs text-outline-variant"
                data-testid="register-resend-ack"
                role="status"
              >
                {t('register.success.resend_ack')}
              </p>
            ) : (
              <p className="text-xs text-outline-variant">
                {t('register.success.resend_hint')}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  void handleResend();
                }}
                disabled={resendSubmitting}
                data-testid="register-resend-submit"
                className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low disabled:opacity-40 disabled:pointer-events-none"
              >
                {resendSubmitting ? t('register.resend.submitting') : t('register.resend.submit')}
              </button>
              <Link
                to="/login"
                data-testid="register-success-login"
                className="text-xs font-bold uppercase tracking-[0.08em] text-outline hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low"
              >
                {t('common.back_to_login')}
              </Link>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('register.email')}
              </span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={submitting}
                required
                data-testid="register-email"
                className={INPUT_CLASS_ON_LOWEST}
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('register.password_with_hint')}
              </span>
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                disabled={submitting}
                required
                minLength={10}
                maxLength={128}
                data-testid="register-password"
                className={INPUT_CLASS_ON_LOWEST}
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('register.workspace_name')}
              </span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
                disabled={submitting}
                maxLength={80}
                placeholder={t('register.workspace_placeholder')}
                data-testid="register-display-name"
                className={INPUT_CLASS_ON_LOWEST}
              />
            </label>
            {error !== null ? (
              <p
                className="m-0 text-xs font-medium text-error"
                role="alert"
                data-testid="register-error"
              >
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit"
              className="mt-2 inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
            >
              {submitting ? t('register.submitting') : t('register.submit')}
            </button>
          </form>
        )}
    </AuthPageFrame>
  );
}
